/**
 * Centralized configuration and utilities for onboarding level system
 * Defines level themes, colors, badges, and progress tracking
 */

import { IconRocket, IconTrendingUp, IconTrophy } from "@tabler/icons-react";
import type { OnboardingStepItem, OnboardingStepCategory } from "../types";

export type LevelType = Extract<
  OnboardingStepCategory,
  "basic" | "advanced" | "elite"
>;

export interface LevelConfig {
  name: string;
  color: string;
  badgeColor: string;
  badgeType: "Bronze" | "Silver" | "Gold";
  stars: 1 | 2 | 3;
  icon: typeof IconRocket;
  gradient: string;
}

/**
 * Level configuration defining visual themes and metadata for each level
 */
export const LEVEL_CONFIG: Record<LevelType, LevelConfig> = {
  basic: {
    name: "Beginner",
    color: "#2589ff",
    badgeColor: "blue",
    badgeType: "Bronze",
    stars: 1,
    icon: IconRocket,
    gradient: "linear-gradient(135deg, #2589ff 0%, #1e6fc9 100%)",
  },
  advanced: {
    name: "Advanced",
    color: "#f97316",
    badgeColor: "orange",
    badgeType: "Silver",
    stars: 2,
    icon: IconTrendingUp,
    gradient: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)",
  },
  elite: {
    name: "Elite",
    color: "#9333ea",
    badgeColor: "violet",
    badgeType: "Gold",
    stars: 3,
    icon: IconTrophy,
    gradient: "linear-gradient(135deg, #9333ea 0%, #7e22ce 100%)",
  },
};

/**
 * Get progress for a specific level
 * @param steps All onboarding steps
 * @param level Level to check progress for
 * @return Object with completed and total counts
 */
export const getLevelProgress = (
  steps: OnboardingStepItem[],
  level: LevelType
): { completed: number; total: number } => {
  const levelSteps = steps.filter((s) => s.category === level);
  const completed = levelSteps.filter((s) => s.status === "complete").length;
  return { completed, total: levelSteps.length };
};

/**
 * Check if a level is fully complete
 * @param steps All onboarding steps
 * @param level Level to check
 * @return True if all steps in the level are complete
 */
export const isLevelComplete = (
  steps: OnboardingStepItem[],
  level: LevelType
): boolean => {
  const levelSteps = steps.filter((s) => s.category === level);
  if (levelSteps.length === 0) return false;
  return levelSteps.every((s) => s.status === "complete");
};

/**
 * Get level configuration by level type
 * @param level Level type
 * @return Level configuration
 */
export const getLevelConfig = (level: LevelType): LevelConfig => {
  return LEVEL_CONFIG[level];
};

/**
 * Get completion percentage for a level
 * @param steps All onboarding steps
 * @param level Level to check
 * @return Percentage (0-100)
 */
export const getLevelCompletionPercentage = (
  steps: OnboardingStepItem[],
  level: LevelType
): number => {
  const { completed, total } = getLevelProgress(steps, level);
  if (total === 0) return 0;
  return Math.round((completed / total) * 100);
};
